import { Button as ScButton } from '@/components/ui/button'
import {
  addToast,
  Button,
  cn,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Progress,
  Radio,
  RadioGroup,
  Spinner,
  useDisclosure,
} from '@heroui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import config from 'config'
import { useAuth } from 'hooks/useAuth'
import { AudioLines } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useSetState } from 'react-use'
import { $fetch } from 'utils/fetch'
import RainbowLogo from '../../../assets/gifs/rainbow-logo.gif'
import FileUpload from './FileUpload'
import { guidelines, useStudioStore } from './utils'

const cloningMessages = [
  'ANALYZING VOICE PATTERNS',
  'CAPTURING VOCAL ESSENCE',
  'PROCESSING SPEECH RHYTHMS',
  'EXTRACTING TONAL QUALITIES',
  'SYNTHESIZING VOCAL SIGNATURE',
  'CALIBRATING VOICE IMPRINT',
  'MAPPING SPEECH CHARACTERISTICS',
  'RENDERING VOICE PROFILE',
  'FINALIZING VOICE CLONE',
]

export default function CreateVoiceCloneModal({ children }) {
  const { org } = useAuth()
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const [files, setFiles] = useState([])
  const [progress, setProgress] = useState(0)
  const [currentMessage, setCurrentMessage] = useState('')
  const navigate = useNavigate()
  const setSelectedCloneId = useStudioStore(state => state.setSelectedCloneId)
  const queryClient = useQueryClient()
  const [nameError, setNameError] = useState(null)
  const [state, setState] = useSetState({
    name: '',
    description: '',
    gender: 'male',
  })

  const { mutate: cloneVoice, isPending: isCloning } = useMutation({
    mutationFn: async () => {
      const formData = new FormData()

      Object.entries(state).forEach(([key, value]) => {
        if (value)
          formData.append(key, value)
      })

      formData.append('isBeta', 'true')

      files.forEach((file) => {
        formData.append('audio_samples', file)
      })

      return $fetch('/v1/voices/clone', {
        method: 'POST',
        body: formData,
        baseURL: config.FILE_UPLOAD_URL,
      })
    },
    onSuccess: ({ data }) => {
      setCurrentMessage('FINALIZING VOICE CLONE')
      setProgress(100)
      const newVoice = {
        id: data.voice_id,
        tags: ['Beige Clone'],
        user_id: org?.id,
        ...state,
      }
      queryClient.setQueryData(['voices'], old => [...old, newVoice])
      setSelectedCloneId(newVoice.id)
      onOpenChange(false)
      toast.success('Voice clone created!')
    },
    onError: (error) => {
      console.error('error', error)
      const errorType = error.data?.errors?.[0]?.error
      if (errorType === 'VOICE_LIMIT_REACHED') {
        addToast({
          color: 'danger',
          title: 'Voice Limit Reached',
          description: 'Delete voices or upgrade your plan to clone more.',
          classNames: { base: 'gap-x-0' },
          endContent: (
            <Button size="sm" color="default" className="shrink-0" variant="flat" onPress={() => navigate('/dashboard/infrastructure')}>
              View Plans
            </Button>
          ),
        })
      }
      else {
        addToast({
          color: 'danger',
          title: 'Error creating voice clone',
          description: error.data?.errors?.[0]?.message || 'Ensure the name is unique and the file type and size are valid.',
        })
      }
    },
  })

  // Logarithmic progress effect for voice cloning
  useEffect(() => {
    let progressInterval
    if (isCloning) {
      setProgress(0)
      setCurrentMessage(cloningMessages[0])
      const startTime = Date.now()

      progressInterval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000 // seconds
        // Logarithmic progress that approaches 95% over 30 seconds
        // Formula: 95 * (1 - e^(-elapsed/10))
        const logarithmicProgress = 95 * (1 - Math.exp(-elapsed / 10))
        setProgress(Math.min(logarithmicProgress, 95))

        // Update message based on progress thresholds
        const currentProgress = Math.min(logarithmicProgress, 95)
        const messageIndex = Math.min(
          Math.floor((currentProgress / 95) * cloningMessages.length),
          cloningMessages.length - 1,
        )
        setCurrentMessage(cloningMessages[messageIndex])
      }, 200) // Update every 100ms for smooth animation
    }
    else {
      setProgress(0)
      setCurrentMessage('')
    }

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval)
      }
    }
  }, [isCloning])

  const validateName = async () => {
    if (!state.name) {
      setNameError('Voice name is required.')
    }
    else if (state.name?.length > 30) {
      setNameError('Voice name must be shorter than 30 characters.')
    }
    else {
      try {
        const { result: isAvailable } = await $fetch('/v1/voices/check_name_availability', {
          method: 'POST',
          body: {
            name: state.name,
          },
        })
        if (!isAvailable) {
          setNameError('Voice name must be unique.')
        }
        else {
          setNameError(null)
        }
      }
      catch (error) {
        console.error(error)
      }
    }
  }

  return (
    <>
      {typeof children === 'function'
        ? children({ onPress: onOpen })
        : <div onClick={onOpen}>{children}</div>}

      <Modal
        radius="sm"
        size="5xl"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        hideCloseButton={isCloning}
        isKeyboardDismissDisabled={isCloning}
        isDismissable={!isCloning}
        classNames={{
          footer: 'border-t-2 border-gray-200 flex justify-between items-center',
          body: 'flex flex-row gap-0 p-0 w-full',
          header: 'border-b border-gray-200 p-5 pb-3 bg-black',
          base: cn('trasition-all duration-300', { 'bg-transparent shadow-none': isCloning }),
          backdrop: cn('trasition-all duration-300', { 'bg-black bg-opacity-70': isCloning }),
        }}
      >
        <ModalContent>
          {isCloning
            ? (
                <div className="w-full p-24 flex flex-col justify-center items-center gap-3">
                  <img
                    src={RainbowLogo}
                    alt="Cloning voice..."
                    className="w-1/2"
                  />
                  <Progress
                    value={progress}
                    size="md"
                    radius="sm"
                    className="w-1/2"
                    classNames={{
                      track: 'bg-white/20',
                      indicator: 'bg-white',
                    }}
                  />
                  <div className="flex flex-col items-center gap-2 w-full max-w-md">
                    <p className="font-taurus font-bold text-xl tracking-tight text-white">
                      {currentMessage}
                    </p>
                    <p className="font-taurus font-light text-sm tracking-tight text-white">
                      This may take a few minutes.
                    </p>

                  </div>
                </div>
              )
            : onClose => (
              <>
                <ModalHeader className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <AudioLines size={24} className="text-white" />
                    <h2 className="font-taurus font-semibold text-white text-2xl">Create a voice clone</h2>
                  </span>
                  <p className="text-xs font-taurus font-light tracking-wider text-foreground-400">
                    Create a personalized voice clone using our advanced LLM-based text-to-speech system.
                  </p>
                </ModalHeader>

                <ModalBody>

                  <div className="w-[65%] p-5">
                    <div className="flex flex-col gap-5">
                      <Input
                        isRequired
                        placeholder="Enter a name for your voice"
                        label="Name"
                        variant="bordered"
                        radius="none"
                        labelPlacement="outside"
                        onValueChange={value => setState({ name: value })}
                        onBlur={validateName}
                        isInvalid={!!nameError}
                        errorMessage={nameError}
                        value={state.name}
                      />

                      <Input
                        placeholder="Enter a description for your voice"
                        label="Description"
                        description="Describe the tone, accent and style of this voice"
                        variant="bordered"
                        radius="none"
                        labelPlacement="outside"
                        onValueChange={value => setState({ description: value })}
                        value={state.description}
                      />

                      <RadioGroup
                        onValueChange={value => setState({ gender: value })}
                        value={state.gender}
                        classNames={{
                          base: 'w-full',
                          wrapper: 'flex flex-row gap-4 p-3 rounded-lg border border-gray-200 shadow-sm',
                          label: 'text-sm font-medium text-gray-700 mb-2',
                        }}
                        isRequired
                        defaultValue="male"
                        orientation="horizontal"
                        name="gender"
                        color="primary"
                      >
                        {['male', 'female'].map(gender => (
                          <Radio
                            key={gender}
                            classNames={{
                              base: cn(
                                'flex-1 max-w-full m-0 bg-white hover:bg-gray-50 cursor-pointer rounded-md p-3 border-2 border-transparent',
                                gender === 'male'
                                  ? 'data-[selected=true]:border-primary'
                                  : 'data-[selected=true]:border-secondary',
                              ),
                              wrapper: cn(
                                gender === 'male'
                                  ? 'group-data-[selected=true]:border-primary'
                                  : 'group-data-[selected=true]:border-secondary',
                              ),
                              control: cn(
                                gender === 'male'
                                  ? 'group-data-[selected=true]:bg-primary group-data-[selected=true]:border-primary'
                                  : 'group-data-[selected=true]:bg-secondary group-data-[selected=true]:border-secondary',
                              ),
                              labelWrapper: 'ml-2 flex flex-col gap-1',
                              label: cn(
                                'text-sm capitalize text-gray-800',
                                gender === 'male'
                                  ? 'group-data-[selected=true]:text-primary'
                                  : 'group-data-[selected=true]:text-secondary',
                              ),
                            }}
                            value={gender}
                          >
                            {gender}
                          </Radio>
                        ))}
                      </RadioGroup>

                      <FileUpload
                        fileOrFiles={files}
                        setFileOrFiles={setFiles}
                        multiple
                        types={['wav']}
                        label="Upload Audio Samples"
                        maxSize={10 * 1024 * 1024}
                        maxFiles={5}
                        onSizeError={() => toast.error('Files must be under 3MB in size')}
                      />
                    </div>
                  </div>

                  <div className="w-[35%] p-4  bg-gray-50 border-l border-gray-300">
                    <h3 className="text-gray-500 border-b border-gray-300 pb-2 font-semibold text-base">Guidelines for Best Results</h3>
                    <div>
                      <div className="flex flex-col gap-5 pt-5">
                        {guidelines.map((guideline, index) => {
                          const Icon = guideline.icon
                          return (
                            <div key={index} className="flex items-start gap-3">
                              <Icon size={16} className="shrink-0 mt-1 text-primary-300" />
                              <p className="text-gray-500 text-xs">{guideline.text}</p>
                            </div>
                          )
                        })}
                      </div>

                      <div className="mt-10">
                        <p className="text-2xs font-semibold text-gray-600 mb-1.5">
                          Supported File Types:
                        </p>
                        <div className="flex space-x-1.5">
                          <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full text-2xs">
                            .wav
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </ModalBody>

                <ModalFooter>
                  <ScButton variant="ghost" color="gray" type="button" onClick={onClose}>
                    Cancel
                  </ScButton>
                  <Button
                    onPress={cloneVoice}
                    color="primary"
                    isDisabled={!files?.length}
                    spinner={<Spinner size="sm" variant="spinner" classNames={{ spinnerBars: 'bg-white' }} />}
                    size="sm"
                    isLoading={isCloning}
                  >
                    {isCloning ? 'Cloning Voice...' : 'Clone Voice'}
                  </Button>
                </ModalFooter>
              </>
            )}
        </ModalContent>
      </Modal>
    </>
  )
}
